// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack, useGlobalSearchParams, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { useColorScheme } from '@/components/useColorScheme';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { HeaderUser } from '@/components/HeaderUser';
import { BetaBadge, BetaBadgeOverlay } from '@/components/BetaBadge';
import { BetaInfoModal } from '@/components/BetaInfoModal';
import { BottomMenu } from '@/components/BottomMenu';
import MicButton from '@/components/MicButton';
import { getCurrentTourId } from '@/services/currentTour';
import { subscribeBugReportRequest } from '@/services/bugReportRequest';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useAuthFlow } from '@/hooks/layout/useAuthFlow';
import { useServiceHealth } from '@/hooks/layout/useServiceHealth';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useUserInfoSync } from '@/hooks/layout/useUserInfoSync';
import { useScoreAllHoles, type ScoreAllHolesResult, type ImportFixture } from '@/hooks/dev/useScoreAllHoles';
import { useRoundInfo, type RoundInfoResult } from '@/hooks/dev/useRoundInfo';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useRoleOverrideState, type ViewAsRole } from '@/stores/roleOverrideState';
import { useManageableCourses } from '@/hooks/useManageableCourses';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useGuestSignup } from '@/hooks/useGuestSignup';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { ServiceStatusModal } from '@/components/layout/ServiceStatusModal';
import { DebugModal } from '@/components/layout/DebugModal';
import { createAppLogger, flushDebugLogs } from '@/services/appLogger';
import { getSharedBffWebSocket, type BffInboundMessage } from '@/services/bffWebSocket';
import { useAppUpdatePrompt } from '@/hooks/useAppUpdatePrompt';
import { getCoachMarkStatesForDebug, resetCoachMarksForDebug } from '@/services/coachMarks';
import appColors from '@/theme/colors';
import appFonts from '@/theme/typography';

type BugImageSelection = {
  file: File;
  fileName: string;
  contentType: string;
  size: number;
  previewUrl: string;
  uploadedUrl: string | null;
};

const COMPACT_WEB_HEADER_BREAKPOINT = 430;
// The centered header title only gets the width left over by headerLeft/headerRight,
// so on phone-width screens a 30pt serif title is ellipsized ("Välkomm..."). Step the
// size down with the viewport instead of letting the native header truncate it.
const HEADER_TITLE_FONT_SIZES = [
  { maxWidth: 360, fontSize: 19 },
  { maxWidth: COMPACT_WEB_HEADER_BREAKPOINT, fontSize: 22 },
  { maxWidth: 600, fontSize: 26 },
];
const HEADER_TITLE_FONT_SIZE_DEFAULT = 30;

const VIEW_AS_ROLES: ViewAsRole[] = ['developer', 'admin', 'user-paid', 'user', 'player'];
const VIEW_AS_LABEL_KEYS: Record<ViewAsRole, string> = {
  developer: 'menu.viewAsDeveloper',
  admin: 'menu.viewAsAdmin',
  'user-paid': 'menu.viewAsPaidUser',
  user: 'menu.viewAsUser',
  player: 'menu.viewAsPlayer',
};

export default function RootLayoutNav() {
  const colorScheme = useColorScheme();
  const { t } = useI18n();
  const { user, setUser, sessionHydrated } = useUser();
  const { width } = useWindowDimensions();
  const router = useRouter();
  const segments = useSegments();
  // A shared results link (results=1): the page is the product, so the shell stays out of the way —
  // no bottom menu, no header menus. The pages drop their own controls for the same flag.
  const { isResultsView } = useInviteViewer();
  const { debug: debugParam } = useGlobalSearchParams<{ debug?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugReportDescription, setBugReportDescription] = useState('');
  const [bugReportImage, setBugReportImage] = useState<BugImageSelection | null>(null);
  const [bugReportSubmitting, setBugReportSubmitting] = useState(false);
  const [bugReportError, setBugReportError] = useState<string | null>(null);
  const [debugTourName, setDebugTourName] = useState<string | null>(null);
  const [debugRoundName, setDebugRoundName] = useState<string | null>(null);
  const [debugPlayerId, setDebugPlayerId] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugCoachMarks, setDebugCoachMarks] = useState<Array<{ key: string; shownCount: number; dismissed: boolean }>>([]);
  const [debugCoachMarksLoading, setDebugCoachMarksLoading] = useState(false);
  const topSegment = segments[0];
  const roundId = useRoundState((state) => state.roundId);
  const scorerOnlyMode = useRoundState((state) => state.scorerOnlyMode);
  const roundIdFromSegments = useMemo(() => {
    if (topSegment !== 'round') {
      return null;
    }
    const segmentValue = segments[1];
    return typeof segmentValue === 'string' && segmentValue.length > 0 ? segmentValue : null;
  }, [segments, topSegment]);
  const isHoleRoute = useMemo(() => {
    return topSegment === 'round' && segments[2] === 'hole';
  }, [segments, topSegment]);
  const effectiveRoundId = roundId ?? roundIdFromSegments;
  const debugEnabled = useDebugState((state) => state.enabled);
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const [tourId, setTourId] = useState<string | null>(null);
  const [singleRoundTourIds, setSingleRoundTourIds] = useState<Record<string, boolean>>({});
  const [mysteryRoundIds, setMysteryRoundIds] = useState<Record<string, boolean>>({});
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'frontend-error',
      }),
    [apiBaseUrl, user?.token]
  );
  const { handleRedirect } = useAuthRedirect({ user, setUser });
  useAppUpdatePrompt();

  const { authChecked } = useAuthFlow({ user, setUser, sessionHydrated });
  usePushNotifications(apiBaseUrl, user?.id, user?.token, authChecked);

  const {
    canCheckServiceHealth,
    serviceStatusOpen,
    setServiceStatusOpen,
    serviceStatusMissing,
    serviceStatusCheckedAt,
    serviceStatusHasWarnings,
    serviceStatusFetchFailed,
    serviceStatusFlashOn,
    serviceStatusWarnings,
  } = useServiceHealth({ apiBaseUrl, user });
  const isInviteRoute =
    (segments[0] === 'tour' || segments[0] === 'friend') && segments[1] === 'invite';
  const isPublicInviteView = isInviteRoute && !user?.token;
  const isPublicLandingView = !user?.token && (segments.length === 0 || topSegment === 'index');
  // Screens that render with `headerShown: false` and have no chrome row of their own, so the
  // beta badge cannot ride in the shared header there and is overlaid on the shell instead.
  // The tour editor is header-less too but carries its own badge in its breadcrumb row.
  const isHeaderlessShellView =
    !topSegment || topSegment === 'index' || topSegment === 'login' || topSegment === 'register';
  const isAnonymousMode = !user?.token;
  const { canCreateAccount } = useGuestSignup();
  // The menu button is normally hidden from anonymous and scorer-only screens, which is where a
  // claim guest or an invite viewer spends their whole visit — so the one thing they can do from
  // here, create an account, had nowhere to live. They get the button back for that item alone;
  // LeftMenu shows them nothing else. A shared results link stays chrome-free by design.
  const showHeaderMenu =
    !isResultsView && !isPublicInviteView && (canCreateAccount || (!scorerOnlyMode && !isAnonymousMode));
  const compactWebHeader = Platform.OS === 'web' && width > 0 && width <= COMPACT_WEB_HEADER_BREAKPOINT;
  const headerTitleFontSize = useMemo(() => {
    if (!(width > 0)) return HEADER_TITLE_FONT_SIZE_DEFAULT;
    return (
      HEADER_TITLE_FONT_SIZES.find((step) => width <= step.maxWidth)?.fontSize ?? HEADER_TITLE_FONT_SIZE_DEFAULT
    );
  }, [width]);

  useEffect(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    if (raw === 'true' || raw === '1') {
      setDebugEnabled(true);
    }
  }, [debugParam, setDebugEnabled]);

  useEffect(() => {
    setTourId(getCurrentTourId() ?? null);
  }, [segments]);

  useEffect(() => {
    // Capability check, not a `window` check: React Native defines `window` but no
    // addEventListener, and these are DOM-only events anyway (RN reports unhandled
    // errors through its own global handler).
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

    const formatError = (err: unknown) => {
      if (err instanceof Error) {
        return { message: err.message, stack: err.stack };
      }
      if (typeof err === 'string') {
        return { message: err };
      }
      try {
        return { message: JSON.stringify(err) };
      } catch {
        return { message: String(err) };
      }
    };

    const handleError = (event: ErrorEvent) => {
      const details = formatError(event.error ?? event.message);
      logToBff(
        'frontend.unhandled.error',
        {
          ...details,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const details = formatError(event.reason);
      logToBff(
        'frontend.unhandled.rejection',
        {
          ...details,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.console || typeof window.console.error !== 'function') {
      return;
    }

    const originalConsoleError = window.console.error.bind(window.console);

    const stringifyConsoleValue = (value: unknown): string => {
      if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
      }
      if (typeof value === 'string') {
        return value;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };

    const patchedConsoleError: typeof console.error = (...args: unknown[]) => {
      originalConsoleError(...args);

      try {
        const firstError = args.find((value): value is Error => value instanceof Error);
        logToBff(
          'frontend.console.error',
          {
            summary: args.map(stringifyConsoleValue).join(' | '),
            errorName: firstError?.name,
            errorMessage: firstError?.message,
            stack: firstError?.stack,
            argumentCount: args.length,
            userId: user?.id,
            path: window.location?.pathname ?? '',
            search: window.location?.search ?? '',
            href: window.location?.href ?? '',
            userAgent: navigator?.userAgent ?? '',
          },
          'error'
        );
      } catch {
        // Keep console behavior intact even if forwarding fails.
      }
    };

    window.console.error = patchedConsoleError;
    return () => {
      if (window.console.error === patchedConsoleError) {
        window.console.error = originalConsoleError;
      }
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (!debugEnabled || !user?.token) {
      setDebugTourName(null);
      setDebugRoundName(null);
      setDebugPlayerId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDebugLoading(true);
      try {
        const requests: Array<Promise<Response>> = [];
        if (user.id) {
          requests.push(
            fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (tourId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (roundId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        const responses = await Promise.all(requests);
        let resolvedTourName: string | null = null;
        let resolvedRoundName: string | null = null;
        let resolvedPlayerId: string | null = null;
        for (const response of responses) {
          if (!response.ok) continue;
          const payload = (await response.json()) as { name?: string; tourName?: string; courseName?: string; id?: string };
          if (payload.id && !resolvedPlayerId) {
            resolvedPlayerId = payload.id;
          }
          if (payload.tourName || payload.name) {
            resolvedTourName = payload.tourName ?? payload.name ?? null;
          }
          if (payload.courseName || payload.name) {
            resolvedRoundName = payload.courseName ?? payload.name ?? null;
          }
        }
        if (!cancelled) {
          setDebugTourName(resolvedTourName);
          setDebugRoundName(resolvedRoundName);
          setDebugPlayerId(resolvedPlayerId);
        }
      } catch {
        if (!cancelled) {
          setDebugTourName(null);
          setDebugRoundName(null);
          setDebugPlayerId(null);
        }
      } finally {
        if (!cancelled) setDebugLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugEnabled, roundId, tourId, user?.id, user?.token]);

  // A "single round" is stored as a hidden tour and has no tour-level views, so the
  // Tour menu item is hidden for it. Mirror BottomMenu's lazy, cached lookup.
  useEffect(() => {
    if (!tourId || !user?.token) return;
    if (tourId in singleRoundTourIds) return;
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const hidden = (data.isHidden as boolean | undefined) ?? (data.IsHidden as boolean | undefined) ?? false;
        setSingleRoundTourIds((prev) => ({ ...prev, [tourId]: hidden }));
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, tourId, singleRoundTourIds, user?.token]);

  // The Mystery prize screen only makes sense for a round that actually plays Mystery, so the menu
  // entry is greyed out until we've confirmed the round carries it. Cached per round, like the
  // single-round lookup above.
  useEffect(() => {
    if (!effectiveRoundId || !user?.token) return;
    if (effectiveRoundId in mysteryRoundIds) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${user.token}` };
    // Only a successful lookup may disable the entry. If the round or the definitions can't be
    // read (offline round, transient failure) the entry stays reachable rather than locking the
    // user out of a setup screen we simply failed to ask about.
    const fallBackToEnabled = () => {
      if (!cancelled) {
        setMysteryRoundIds((prev) => ({ ...prev, [effectiveRoundId]: true }));
      }
    };
    Promise.all([
      fetch(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, { headers }),
      fetch(`${apiBaseUrl}/api/game-definitions`, { headers }),
    ])
      .then(async ([roundResponse, gamesResponse]) => {
        if (!roundResponse.ok || !gamesResponse.ok) {
          fallBackToEnabled();
          return;
        }
        const round = (await roundResponse.json()) as Record<string, unknown>;
        const games = (await gamesResponse.json()) as Array<Record<string, unknown>>;
        const selectedGameIds = new Set(
          ((round.gameDefinitionIds ?? round.GameDefinitionIds ?? []) as unknown[]).filter(
            (id): id is string => typeof id === 'string' && id.length > 0
          )
        );
        // Matched by name, not by a hard-coded GUID: the Mystery definition carries different ids
        // across environments (see the golf-games admin ids in production).
        const hasMystery = (Array.isArray(games) ? games : []).some(
          (game) =>
            selectedGameIds.has(String(game.id ?? game.Id ?? '')) &&
            String(game.name ?? game.Name ?? '').toLowerCase() === 'mystery'
        );
        if (!cancelled) {
          setMysteryRoundIds((prev) => ({ ...prev, [effectiveRoundId]: hasMystery }));
        }
      })
      .catch(fallBackToEnabled);
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, effectiveRoundId, mysteryRoundIds, user?.token]);

  useEffect(() => {
    if (!debugOpen) return;
    let cancelled = false;
    const loadCoachMarks = async () => {
      setDebugCoachMarksLoading(true);
      try {
        const states = await getCoachMarkStatesForDebug(apiBaseUrl);
        if (!cancelled) {
          setDebugCoachMarks(
            states.map((state) => ({
              key: state.key,
              shownCount: state.shownCount,
              dismissed: state.dismissed,
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setDebugCoachMarks([]);
        }
      } finally {
        if (!cancelled) {
          setDebugCoachMarksLoading(false);
        }
      }
    };
    void loadCoachMarks();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugOpen]);

  const buildGuid = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const random = `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`.padEnd(32, '0').slice(0, 32);
    return `${random.slice(0, 8)}-${random.slice(8, 12)}-${random.slice(12, 16)}-${random.slice(16, 20)}-${random.slice(20, 32)}`;
  }, []);

  const clearBugImage = useCallback(() => {
    setBugReportImage((prev) => {
      if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  }, []);

  const selectBugImageFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setBugReportError(t('menu.reportBugInvalidImage'));
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        setBugReportError(t('menu.reportBugImageTooLarge'));
        return;
      }
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        setBugReportError(t('menu.reportBugFileUnsupported'));
        return;
      }
      setBugReportError(null);
      setBugReportImage((prev) => {
        if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return {
          file,
          fileName: file.name || 'bug-report-image.png',
          contentType: file.type || 'image/png',
          size: file.size,
          previewUrl: URL.createObjectURL(file),
          uploadedUrl: null,
        };
      });
    },
    [t]
  );

  const requestBugImagePresign = useCallback(
    async (file: File): Promise<{ uploadUrl: string; publicUrl: string }> => {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      return await new Promise((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.image.presign.timeout'));
          });
        }, 12000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.image.presign.ack') {
            const payload = message as { uploadUrl?: string; publicUrl?: string };
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              if (!payload.uploadUrl || !payload.publicUrl) {
                reject(new Error('bug.image.presign.invalidAck'));
                return;
              }
              resolve({ uploadUrl: payload.uploadUrl, publicUrl: payload.publicUrl });
            });
          } else if (message.type === 'bug.image.presign.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.image.presign.failed'));
            });
          }
        });
        // 'queued' is not a failure — the frame is flushed when the socket (re)opens; the
        // timeout above is the only thing that rejects an unanswered presign.
        ws.sendBugImagePresign({
          requestId,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        });
      });
    },
    [apiBaseUrl, buildGuid, user?.token]
  );

  const ensureBugImageUploaded = useCallback(async () => {
    if (!bugReportImage) {
      return null;
    }
    if (bugReportImage.uploadedUrl) {
      return bugReportImage.uploadedUrl;
    }
    const { uploadUrl, publicUrl } = await requestBugImagePresign(bugReportImage.file);
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': bugReportImage.contentType || 'image/png',
        // Signed into the presigned URL (X-Amz-SignedHeaders) — S3 rejects the PUT without it.
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
      body: bugReportImage.file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`bug.image.upload.failed.${uploadResponse.status}`);
    }
    setBugReportImage((prev) => (prev ? { ...prev, uploadedUrl: publicUrl } : prev));
    return publicUrl;
  }, [bugReportImage, requestBugImagePresign]);

  const loadBugReportRoundContext = useCallback(async () => {
    if (!effectiveRoundId || !user?.token) {
      return undefined;
    }

    const headers = { Authorization: `Bearer ${user.token}` };
    const fetchJsonSafe = async (url: string): Promise<unknown> => {
      try {
        const response = await fetch(url, { headers });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    };

    const [roundPayload, playersPayload, scoreboardPayload, holeEntriesPayload, holeEventsPayload, gameDefinitionsPayload, tourLeaderboardPayload] =
      await Promise.all([
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/players`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/scoreboard`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-entries`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-events`),
        fetchJsonSafe(`${apiBaseUrl}/api/game-definitions`),
        tourId ? fetchJsonSafe(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`) : Promise.resolve(null),
      ]);

    const round = (roundPayload ?? {}) as {
      gameDefinitionIds?: string[];
      GameDefinitionIds?: string[];
      status?: string;
      Status?: string;
      name?: string;
      Name?: string;
      courseName?: string;
      CourseName?: string;
    };
    const gameDefinitionIds = round.gameDefinitionIds ?? round.GameDefinitionIds ?? [];

    const gameDefinitions = Array.isArray(gameDefinitionsPayload)
      ? (gameDefinitionsPayload as Array<{ id?: string; name?: string; competitionType?: string }>)
          .filter((item) => !!item?.id && gameDefinitionIds.includes(item.id as string))
          .map((item) => ({
            id: item.id,
            name: item.name,
            competitionType: item.competitionType,
          }))
      : [];

    const playersRaw = Array.isArray((playersPayload as { players?: unknown[] })?.players)
      ? ((playersPayload as { players?: unknown[] }).players as Array<{ id?: string; name?: string }>)
      : Array.isArray(playersPayload)
        ? (playersPayload as Array<{ id?: string; name?: string }>)
        : [];
    const players = playersRaw.map((player) => ({ id: player.id, name: player.name }));

    const holeEntries = Array.isArray(holeEntriesPayload) ? (holeEntriesPayload as Array<Record<string, unknown>>) : [];
    const holeScoringByHole = Object.values(
      holeEntries.reduce<Record<string, { hole: number; entries: Array<Record<string, unknown>> }>>((acc, entry) => {
        const hole =
          (typeof entry.hole === 'number' && Number.isFinite(entry.hole) ? entry.hole : undefined) ??
          (typeof entry.Hole === 'number' && Number.isFinite(entry.Hole) ? entry.Hole : undefined) ??
          (typeof entry.holeNumber === 'number' && Number.isFinite(entry.holeNumber) ? entry.holeNumber : undefined) ??
          (typeof entry.HoleNumber === 'number' && Number.isFinite(entry.HoleNumber) ? entry.HoleNumber : undefined);
        if (!hole) return acc;
        const key = String(hole);
        if (!acc[key]) {
          acc[key] = { hole, entries: [] };
        }
        acc[key].entries.push({
          playerId: entry.playerId ?? entry.PlayerId ?? null,
          playerName: entry.playerName ?? entry.PlayerName ?? null,
          grossStrokes: entry.grossStrokes ?? entry.GrossStrokes ?? entry.grossStroke ?? entry.GrossStroke ?? null,
          pickup: entry.pickup ?? entry.Pickup ?? null,
          scorePoints: entry.scorePoints ?? entry.ScorePoints ?? null,
          roundGamePoints: entry.roundGamePoints ?? entry.RoundGamePoints ?? null,
        });
        return acc;
      }, {})
    ).sort((a, b) => a.hole - b.hole);

    const gameResults =
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.scores ??
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.Scores ??
      (Array.isArray(scoreboardPayload) ? scoreboardPayload : null);

    return {
      roundId: effectiveRoundId,
      roundName: round.name ?? round.Name ?? null,
      courseName: round.courseName ?? round.CourseName ?? null,
      roundStatus: round.status ?? round.Status ?? null,
      playerCount: players.length,
      players,
      gamesIncluded: {
        gameDefinitionIds,
        definitions: gameDefinitions,
      },
      leaderboard: tourLeaderboardPayload ?? undefined,
      gameResults: gameResults ?? undefined,
      scoringPerHole: holeScoringByHole,
      holeEvents: Array.isArray(holeEventsPayload) ? holeEventsPayload : undefined,
    };
  }, [apiBaseUrl, effectiveRoundId, tourId, user?.token]);

  const submitBugReport = useCallback(async () => {
    const description = bugReportDescription.trim();
    if (!description && !bugReportImage) {
      setBugReportError(t('menu.reportBugMissingContent'));
      return;
    }
    setBugReportSubmitting(true);
    setBugReportOpen(false);
    setBugReportError(null);
    try {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      const imageUrl = await ensureBugImageUploaded();
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      let roundContext: unknown;
      try {
        roundContext = await loadBugReportRoundContext();
      } catch (error) {
        roundContext = {
          error: error instanceof Error ? error.message : String(error),
        };
      }
      // The debug lines the phone has been keeping to itself go with the report, tagged with its
      // requestId so they can be found next to it in the logs.
      const debugLogsFlushed = flushDebugLogs(ws, 'bug.report', { bugReportRequestId: requestId });
      const context = {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        roundId: effectiveRoundId ?? undefined,
        tourId: tourId ?? undefined,
        debugEnabled,
        platform: Platform.OS,
        viewport:
          typeof window !== 'undefined'
            ? { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio }
            : undefined,
        roundContext,
        debugLogsFlushed,
      };

      await new Promise<void>((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.report.timeout'));
          });
        }, 15000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.report.ack') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              resolve();
            });
          } else if (message.type === 'bug.report.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.report.failed'));
            });
          }
        });
        // 'queued' is not a failure — the frame is flushed when the socket (re)opens; the
        // timeout above is the only thing that rejects an unanswered report.
        ws.sendBugReport({
          requestId,
          description: description || undefined,
          imageUrl: imageUrl ?? undefined,
          pageUrl,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          appVersion: typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_APP_VERSION : undefined,
          timestamp: new Date().toISOString(),
          context,
        });
      });

      setBugReportDescription('');
      clearBugImage();
    } catch (error) {
      setBugReportError(
        `${t('menu.reportBugSendFailed')}: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setBugReportSubmitting(false);
    }
  }, [
    apiBaseUrl,
    bugReportDescription,
    bugReportImage,
    buildGuid,
    clearBugImage,
    debugEnabled,
    effectiveRoundId,
    ensureBugImageUploaded,
    loadBugReportRoundContext,
    t,
    tourId,
    user?.token,
  ]);

  const openBugFilePicker = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setBugReportError(t('menu.reportBugFileUnsupported'));
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      selectBugImageFile(file);
    };
    input.click();
  }, [selectBugImageFile, t]);

  const bugReportImageMeta = useMemo(() => {
    if (!bugReportImage) {
      return '';
    }
    return `${bugReportImage.fileName} (${Math.round(bugReportImage.size / 1024)} KB)`;
  }, [bugReportImage]);

  useUserInfoSync({ apiBaseUrl, user, setUser });

  // Gates the developer-only "Score all holes"/"Round information" actions and the "Golf
  // courses" menu. Effective roles, not raw token roles: a developer can simulate another
  // role from the avatar menu, and this is where that simulation takes effect.
  const { isDeveloper, isAdmin, canViewAs, viewAsRole } = useEffectiveRoles(user);
  // "View as role" lives in the admin dropdown, and both are gated on the *real*
  // developer/admin roles (canViewAs), never the simulated ones — otherwise picking
  // "Player" would hide the only way back.
  const setRoleOverride = useRoleOverrideState((state) => state.setOverride);
  const clearRoleOverride = useRoleOverrideState((state) => state.clearOverride);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const applyViewAs = useCallback(
    (role: ViewAsRole | null) => {
      if (!user?.id) return;
      if (role) {
        setRoleOverride(user.id, role);
      } else {
        clearRoleOverride();
      }
      setViewAsOpen(false);
    },
    [clearRoleOverride, setRoleOverride, user?.id]
  );
  // "Golf courses" management menu: shown to admins and to anyone who has created a course.
  // The created-a-course half comes from the server, which does not know about the
  // simulation, so hide it explicitly while viewing as a plain player.
  const { courses: manageableCourses } = useManageableCourses(user?.token);
  const canManageCourses = viewAsRole === 'player' ? false : isAdmin || manageableCourses.length > 0;
  const { scoreAllHoles, importAndScoreAllHoles, isRunning: scoreAllHolesRunning, status: scoreAllHolesStatus } = useScoreAllHoles({
    apiBaseUrl,
    token: user?.token,
    roundId: effectiveRoundId,
  });
  const [scoreAllHolesResult, setScoreAllHolesResult] = useState<ScoreAllHolesResult | null>(null);
  const [scoreAllHolesError, setScoreAllHolesError] = useState<string | null>(null);
  const [scoreAllHolesConfigOpen, setScoreAllHolesConfigOpen] = useState(false);
  const [holesToScoreText, setHolesToScoreText] = useState('18');
  const [cleanBeforeImport, setCleanBeforeImport] = useState(false);
  // Admin-only shortcut to the Game Calculator. It is a separate web app behind the same
  // public host — Envoy routes /goffy-admin to it and gates /goffy-admin/api on the `admin`
  // realm role — with its own Keycloak client, so it runs its own login rather than
  // inheriting this session's token. Opened in a new tab/browser to keep the app in place.
  const openGameCalculator = useCallback(
    (page = '') => {
      setAdminMenuOpen(false);
      const url = `${apiBaseUrl.replace(/\/+$/, '')}/goffy-admin/${page}`;
      if (Platform.OS === 'web') {
        if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      void WebBrowser.openBrowserAsync(url).catch(() => undefined);
    },
    [apiBaseUrl]
  );
  const openScoreAllHoles = useCallback(() => {
    console.info('[score-all-holes] menu click', { roundId: effectiveRoundId, hasToken: !!user?.token });
    setScoreAllHolesResult(null);
    setScoreAllHolesError(null);
    setHolesToScoreText('18');
    setCleanBeforeImport(false);
    setScoreAllHolesConfigOpen(true);
    setAdminMenuOpen(false);
  }, [effectiveRoundId, user?.token]);
  const runScoreAllHoles = useCallback(async () => {
    const parsed = parseInt(holesToScoreText, 10);
    const count = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    setScoreAllHolesConfigOpen(false);
    setScoreAllHolesResult(null);
    setScoreAllHolesError(null);
    try {
      const result = await scoreAllHoles(count);
      setScoreAllHolesResult(result);
    } catch (err) {
      setScoreAllHolesError(err instanceof Error ? err.message : t('menu.scoreAllHolesError'));
    }
  }, [scoreAllHoles, t, holesToScoreText]);
  // Optional: import a fetch-round-scores.py export and replay it onto the round.
  const openScoreAllHolesImport = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setScoreAllHolesConfigOpen(false);
      setScoreAllHolesError(t('menu.reportBugFileUnsupported'));
      return;
    }
    const parsed = parseInt(holesToScoreText, 10);
    const count = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.json5,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      setScoreAllHolesConfigOpen(false);
      setScoreAllHolesResult(null);
      setScoreAllHolesError(null);
      void (async () => {
        try {
          const text = await file.text();
          const fixture = JSON.parse(text) as ImportFixture;
          const result = await importAndScoreAllHoles(fixture, count, cleanBeforeImport);
          setScoreAllHolesResult(result);
        } catch (err) {
          setScoreAllHolesError(err instanceof Error ? err.message : t('menu.scoreAllHolesImportError'));
        }
      })();
    };
    input.click();
  }, [holesToScoreText, importAndScoreAllHoles, cleanBeforeImport, t]);

  // Developer-only "Round information" debug view (hamburger menu): shows the full round + holes +
  // players + subgames payloads (including ids) in a scrollable modal.
  const { loadRoundInfo, isRunning: roundInfoLoading } = useRoundInfo({
    apiBaseUrl,
    token: user?.token,
    roundId: effectiveRoundId,
  });
  const [roundInfoOpen, setRoundInfoOpen] = useState(false);
  const [roundInfoResult, setRoundInfoResult] = useState<RoundInfoResult | null>(null);
  const [roundInfoError, setRoundInfoError] = useState<string | null>(null);
  const roundInfoCopyText = useMemo(() => {
    if (!roundInfoResult) return '';
    const r = roundInfoResult;
    const lines: string[] = [];
    lines.push(`Round: ${r.name ?? '—'}${r.status ? ` (${r.status})` : ''}`);
    lines.push(`Round id: ${r.roundId}`);
    if (r.tourId) lines.push(`Tour id: ${r.tourId}`);
    lines.push(`Course: ${r.courseName ?? '—'}${r.courseId ? ` (${r.courseId})` : ''}`);
    if (r.playDate) lines.push(`Play date: ${r.playDate}`);
    lines.push(
      `Holes: ${r.holesCount ?? r.holes.length} · Players: ${r.players.length} · Games: ${r.games.length} · Subgames: ${r.subgameCount}`
    );
    lines.push('', 'Games:');
    r.games.forEach((g) =>
      lines.push(`- ${g.name ?? '—'}${g.weight !== null ? ` · ${g.weight}%` : ''} · ${g.id}`)
    );
    lines.push('', 'Players:');
    r.players.forEach((p) => lines.push(`- ${p.name} · SCP ${p.scp ?? '—'} · ${p.id}`));
    if (r.gamePoints.length) {
      lines.push('', 'Game points:');
      r.gamePoints.forEach((game) => {
        lines.push(`${game.gameName}:`);
        game.rows.forEach((row) => lines.push(`  - ${row.playerName}: ${row.points}`));
      });
    }
    lines.push('', 'Holes:');
    r.holes.forEach((h) =>
      lines.push(`- #${h.number} · par ${h.par ?? '—'} · SI ${h.strokeIndex ?? '—'}${h.id ? ` · ${h.id}` : ''}`)
    );
    return lines.join('\n');
  }, [roundInfoResult]);
  const openRoundInfo = useCallback(async () => {
    setAdminMenuOpen(false);
    setRoundInfoResult(null);
    setRoundInfoError(null);
    setRoundInfoOpen(true);
    try {
      const result = await loadRoundInfo();
      setRoundInfoResult(result);
    } catch (err) {
      setRoundInfoError(err instanceof Error ? err.message : t('menu.roundInfoError'));
    }
  }, [loadRoundInfo, t]);
  const copyRoundInfo = useCallback(() => {
    if (!roundInfoCopyText) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(roundInfoCopyText).catch(() => undefined);
    }
  }, [roundInfoCopyText]);

  useEffect(() => {
    if ((isPublicInviteView || isPublicLandingView || topSegment === 'login' || topSegment === 'register') && (leftMenuOpen || adminMenuOpen)) {
      setLeftMenuOpen(false);
      setAdminMenuOpen(false);
    }
  }, [adminMenuOpen, isPublicInviteView, isPublicLandingView, leftMenuOpen, topSegment]);

  // Screens rendered without the global header (tour edit) reach the bug-report
  // modal through this request channel instead of a prop.
  useEffect(() => {
    return subscribeBugReportRequest(() => {
      setBugReportError(null);
      setBugReportOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!bugReportOpen) return;
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) {
        return;
      }
      for (const item of Array.from(items)) {
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        selectBugImageFile(file);
        break;
      }
    };

    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
    };
  }, [bugReportOpen, selectBugImageFile]);

  useEffect(() => {
    return () => {
      clearBugImage();
    };
  }, [clearBugImage]);

  const handleResetCoachMarks = useCallback(async () => {
    await resetCoachMarksForDebug(apiBaseUrl);
    const states = await getCoachMarkStatesForDebug(apiBaseUrl);
    setDebugCoachMarks(
      states.map((state) => ({
        key: state.key,
        shownCount: state.shownCount,
        dismissed: state.dismissed,
      }))
    );
  }, [apiBaseUrl]);

  if (!authChecked) {
    return null;
  }

  const isTourLeaderboardRoute = topSegment === 'tour' && segments[2] === 'leaderboard';
  const isRoundSummaryRoute = topSegment === 'round' && segments[2] === 'summary';
  const isRoundScorecardRoute = topSegment === 'round' && segments[2] === 'scorecard';
  const showBottomMenu =
    !isPublicInviteView &&
    !isPublicLandingView &&
    !isResultsView &&
    (isHoleRoute || isTourLeaderboardRoute || isRoundSummaryRoute || isRoundScorecardRoute);
  // Role gates hide an entry outright; state gates only grey it out. Scorer-only is a role gate,
  // so the round/tour settings entries disappear for scorers rather than showing as unavailable.
  const canOpenRoundSettings = !!effectiveRoundId;
  const roundSettingsPath = effectiveRoundId
    ? `/round/${effectiveRoundId}/roundstart?mode=update${debugEnabled ? '&debug=true' : ''}`
    : '/my-tours';
  const isSingleRoundTour = !!tourId && singleRoundTourIds[tourId] === true;
  const canOpenMysteryPrize = !!effectiveRoundId && mysteryRoundIds[effectiveRoundId] === true;
  const isMobileView = Platform.OS !== 'web' || compactWebHeader;
  const tourPath = tourId
    ? isMobileView
      ? `/my-tours${debugEnabled ? '?debug=true' : ''}`
      : `/tour/${tourId}/edit${debugEnabled ? '?debug=true' : ''}`
    : `/my-tours${debugEnabled ? '?debug=true' : ''}`;

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <View style={styles.shell}>
        <View style={styles.stack}>
          <ServiceStatusModal
            open={serviceStatusOpen}
            onClose={() => setServiceStatusOpen(false)}
            missing={serviceStatusMissing}
            checkedAt={serviceStatusCheckedAt}
            hasWarnings={serviceStatusHasWarnings}
            warnings={serviceStatusWarnings}
          />
          <DebugModal
            open={debugOpen}
            onClose={() => setDebugOpen(false)}
            user={user}
            playerId={debugPlayerId}
            roundId={roundId}
            tourId={tourId}
            roundName={debugRoundName}
            tourName={debugTourName}
            loading={debugLoading}
            coachMarks={debugCoachMarks}
            coachMarksLoading={debugCoachMarksLoading}
            onDisable={() => {
              setDebugEnabled(false);
              setDebugOpen(false);
            }}
            onResetCoachMarks={handleResetCoachMarks}
          />
          <Modal
            transparent
            visible={!isPublicInviteView && leftMenuOpen}
            animationType="fade"
            onRequestClose={() => setLeftMenuOpen(false)}
          >
            <LeftMenu
              open={leftMenuOpen}
              isHoleRoute={isHoleRoute}
              canOpenRoundSettings={canOpenRoundSettings}
              roundSettingsPath={roundSettingsPath}
              scorerOnlyMode={scorerOnlyMode}
              isSingleRound={isSingleRoundTour}
              mysteryPath={effectiveRoundId ? `/round/${effectiveRoundId}/mystery-prize?tourId=${tourId ?? ''}&debug=${debugEnabled ? 'true' : 'false'}` : '/round'}
              canOpenMysteryPrize={canOpenMysteryPrize}
              tourPath={tourPath}
              canOpenTourLeaderboard={!!tourId}
              tourLeaderboardPath={tourId ? `/tour/${tourId}/leaderboard` : '/my-tours'}
              canManageCourses={canManageCourses}
              canCreateAccount={canCreateAccount}
              onOpenBugReport={() => {
                setLeftMenuOpen(false);
                setBugReportError(null);
                setBugReportOpen(true);
              }}
              onClose={() => setLeftMenuOpen(false)}
            />
          </Modal>
          <Modal
            transparent
            visible={!isPublicInviteView && adminMenuOpen}
            animationType="fade"
            onRequestClose={() => setAdminMenuOpen(false)}
          >
            <AdminMenu
              open={adminMenuOpen}
              isAdmin={isAdmin}
              isDeveloper={isDeveloper}
              onGameCalculator={() => openGameCalculator()}
              onGolfGamesAdmin={() => openGameCalculator('golf-games-admin.html')}
              scoreAllHolesEnabled={isDeveloper && !!effectiveRoundId}
              scoreAllHolesRunning={scoreAllHolesRunning}
              scoreAllHolesStatus={scoreAllHolesStatus}
              onScoreAllHoles={openScoreAllHoles}
              roundInfoEnabled={isDeveloper && !!effectiveRoundId}
              roundInfoLoading={roundInfoLoading}
              onRoundInfo={openRoundInfo}
              viewAsRole={viewAsRole}
              onViewAs={() => {
                setAdminMenuOpen(false);
                setViewAsOpen(true);
              }}
              onClose={() => setAdminMenuOpen(false)}
            />
          </Modal>
          <Modal visible={viewAsOpen} transparent animationType="fade" onRequestClose={() => setViewAsOpen(false)}>
            <Pressable style={styles.leftMenuBackdrop} onPress={() => setViewAsOpen(false)}>
              <Pressable style={styles.bugModalCard} onPress={() => null}>
                <Text style={styles.bugModalTitle}>{t('menu.viewAsTitle')}</Text>
                <Text style={styles.bugModalHint}>{t('menu.viewAsHint')}</Text>
                {VIEW_AS_ROLES.map((role) => (
                  <Pressable
                    key={role}
                    style={styles.leftMenuItem}
                    testID={`view-as-${role}`}
                    onPress={() => applyViewAs(role)}
                  >
                    <Text style={[styles.leftMenuItemText, viewAsRole === role && styles.viewAsActiveText]}>
                      {t(VIEW_AS_LABEL_KEYS[role])}
                    </Text>
                  </Pressable>
                ))}
                <Pressable style={styles.leftMenuItem} testID="view-as-reset" onPress={() => applyViewAs(null)}>
                  <Text style={[styles.leftMenuItemText, !viewAsRole && styles.viewAsActiveText]}>
                    {t('menu.viewAsReset')}
                  </Text>
                </Pressable>
                <Pressable style={styles.scoreModalOkButton} onPress={() => setViewAsOpen(false)}>
                  <Text style={styles.scoreModalOkText}>{t('menu.close')}</Text>
                </Pressable>
              </Pressable>
            </Pressable>
          </Modal>
          <Modal
            transparent
            visible={scoreAllHolesConfigOpen || scoreAllHolesRunning || !!scoreAllHolesResult || !!scoreAllHolesError}
            animationType="fade"
            onRequestClose={() => {
              if (scoreAllHolesRunning) return;
              setScoreAllHolesConfigOpen(false);
              setScoreAllHolesResult(null);
              setScoreAllHolesError(null);
            }}
          >
            <View style={styles.leftMenuBackdrop}>
              <View style={styles.bugModalCard}>
                <Text style={styles.bugModalTitle}>{t('menu.scoreAllHoles')}</Text>
                {scoreAllHolesConfigOpen ? (
                  <>
                    <Text style={styles.bugModalHint}>{t('menu.scoreAllHolesCountLabel')}</Text>
                    <TextInput
                      value={holesToScoreText}
                      onChangeText={setHolesToScoreText}
                      keyboardType="number-pad"
                      style={styles.scoreModalInput}
                      placeholder="18"
                      placeholderTextColor={appColors.text.muted}
                    />
                    <Pressable
                      style={styles.scoreModalCheckboxRow}
                      onPress={() => setCleanBeforeImport((prev) => !prev)}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: cleanBeforeImport }}
                    >
                      <View style={[styles.scoreModalCheckbox, cleanBeforeImport && styles.scoreModalCheckboxChecked]}>
                        {cleanBeforeImport ? <Text style={styles.scoreModalCheckboxMark}>✓</Text> : null}
                      </View>
                      <Text style={styles.scoreModalCheckboxLabel}>{t('menu.scoreAllHolesClean')}</Text>
                    </Pressable>
                    <Text style={styles.scoreModalImportHint}>{t('menu.scoreAllHolesImportHint')}</Text>
                    <View style={styles.scoreModalButtonRow}>
                      <Pressable style={styles.scoreModalSecondaryButton} onPress={() => setScoreAllHolesConfigOpen(false)}>
                        <Text style={styles.scoreModalOkText}>{t('menu.close')}</Text>
                      </Pressable>
                      <Pressable style={styles.scoreModalSecondaryButton} onPress={openScoreAllHolesImport}>
                        <Text style={styles.scoreModalOkText}>{t('menu.scoreAllHolesImport')}</Text>
                      </Pressable>
                      <Pressable style={styles.scoreModalOkButton} onPress={runScoreAllHoles}>
                        <Text style={styles.scoreModalOkText}>{t('menu.scoreAllHolesStart')}</Text>
                      </Pressable>
                    </View>
                  </>
                ) : scoreAllHolesError ? (
                  <>
                    <Text style={styles.scoreModalError}>{scoreAllHolesError}</Text>
                    <Pressable
                      style={styles.scoreModalOkButton}
                      onPress={() => {
                        setScoreAllHolesResult(null);
                        setScoreAllHolesError(null);
                      }}
                    >
                      <Text style={styles.scoreModalOkText}>{t('menu.ok')}</Text>
                    </Pressable>
                  </>
                ) : !scoreAllHolesResult ? (
                  <Text style={styles.bugModalHint}>{scoreAllHolesStatus ?? t('menu.scoreAllHolesRunning')}</Text>
                ) : (
                  <>
                    <Text style={styles.bugModalHint}>
                      {t('menu.scoreAllHolesDone', {
                        scores: scoreAllHolesResult.summary.scores,
                        holes: scoreAllHolesResult.summary.holes,
                        players: scoreAllHolesResult.summary.players,
                        events: scoreAllHolesResult.summary.events,
                        mystery: scoreAllHolesResult.summary.mystery,
                      })}
                    </Text>
                    <ScrollView style={styles.scoreModalScroll}>
                      {scoreAllHolesResult.holes.map((hole) => (
                        <View key={`score-hole-${hole.hole}`} style={styles.scoreModalHole}>
                          <Text style={styles.scoreModalHoleTitle}>
                            {t('menu.scoreAllHolesHole', { hole: hole.hole, par: hole.par })}
                          </Text>
                          {hole.scores.map((score) => {
                            const suffix = score.events.length ? ` · ${score.events.join(', ')}` : '';
                            const line = `${t('menu.scoreAllHolesRow', { name: score.playerName, strokes: score.strokes })}${suffix}`;
                            return (
                              <Text key={`score-hole-${hole.hole}-${score.playerId}`} style={styles.scoreModalRow}>
                                {line}
                              </Text>
                            );
                          })}
                        </View>
                      ))}
                      {scoreAllHolesResult.mystery.length ? (
                        <View style={styles.scoreModalHole}>
                          <Text style={styles.scoreModalHoleTitle}>{t('menu.scoreAllHolesMystery')}</Text>
                          {scoreAllHolesResult.mystery.map((entry, index) => (
                            <Text key={`score-mystery-${index}`} style={styles.scoreModalRow}>
                              {t('menu.scoreAllHolesMysteryRow', { hole: entry.hole, name: entry.playerName })}
                            </Text>
                          ))}
                        </View>
                      ) : null}
                    </ScrollView>
                    <Pressable
                      style={styles.scoreModalOkButton}
                      onPress={() => {
                        setScoreAllHolesResult(null);
                        setScoreAllHolesError(null);
                      }}
                    >
                      <Text style={styles.scoreModalOkText}>{t('menu.ok')}</Text>
                    </Pressable>
                  </>
                )}
              </View>
            </View>
          </Modal>
          <Modal
            transparent
            visible={roundInfoOpen}
            animationType="fade"
            onRequestClose={() => setRoundInfoOpen(false)}
          >
            <View style={styles.leftMenuBackdrop}>
              <View style={styles.bugModalCard}>
                <Text style={styles.bugModalTitle}>{t('menu.roundInfo')}</Text>
                {roundInfoError ? (
                  <Text style={styles.scoreModalError}>{roundInfoError}</Text>
                ) : !roundInfoResult ? (
                  <Text style={styles.bugModalHint}>{t('menu.roundInfoLoading')}</Text>
                ) : (
                  <ScrollView style={styles.scoreModalScroll}>
                    {/* eslint-disable i18n/no-literal-text -- admin round diagnostics; the labels
                        mirror copyRoundInfo's text, which is pasted into bug reports as-is */}
                    <Text selectable style={styles.roundInfoHeading}>
                      {roundInfoResult.name ?? '—'}
                      {roundInfoResult.status ? ` · ${roundInfoResult.status}` : ''}
                    </Text>
                    <View style={styles.scoreModalHole}>
                      <Text selectable style={styles.roundInfoRow}>{`Round id: ${roundInfoResult.roundId}`}</Text>
                      {roundInfoResult.tourId ? (
                        <Text selectable style={styles.roundInfoRow}>{`Tour id: ${roundInfoResult.tourId}`}</Text>
                      ) : null}
                      <Text selectable style={styles.roundInfoRow}>
                        {`Course: ${roundInfoResult.courseName ?? '—'}${roundInfoResult.courseId ? `  (${roundInfoResult.courseId})` : ''}`}
                      </Text>
                      {roundInfoResult.playDate ? (
                        <Text selectable style={styles.roundInfoRow}>{`Play date: ${roundInfoResult.playDate}`}</Text>
                      ) : null}
                      <Text selectable style={styles.roundInfoRow}>
                        {`Holes: ${roundInfoResult.holesCount ?? roundInfoResult.holes.length}  ·  Players: ${roundInfoResult.players.length}  ·  Games: ${roundInfoResult.games.length}  ·  Subgames: ${roundInfoResult.subgameCount}`}
                      </Text>
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Games (${roundInfoResult.games.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.games.length === 0 ? (
                        <Text style={styles.roundInfoRow}>—</Text>
                      ) : (
                        roundInfoResult.games.map((game) => (
                          <Text key={`round-info-game-${game.id}`} selectable style={styles.roundInfoRow}>
                            {`${game.name ?? '—'}${game.weight !== null ? `  ·  ${game.weight}%` : ''}  ·  ${game.id}`}
                          </Text>
                        ))
                      )}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Players (${roundInfoResult.players.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.players.map((player) => (
                        <Text key={`round-info-player-${player.id}`} selectable style={styles.roundInfoRow}>
                          {`${player.name}  ·  SCP ${player.scp ?? '—'}  ·  ${player.id}`}
                        </Text>
                      ))}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Game points (${roundInfoResult.gamePoints.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.gamePoints.length === 0 ? (
                        <Text style={styles.roundInfoRow}>—</Text>
                      ) : (
                        roundInfoResult.gamePoints.map((game) => (
                          <View key={`round-info-gamepoints-${game.gameId}`} style={styles.roundInfoGameBlock}>
                            <Text selectable style={styles.roundInfoGameName}>{game.gameName}</Text>
                            {game.rows.map((row) => (
                              <Text
                                key={`round-info-gamepoints-${game.gameId}-${row.playerId}`}
                                selectable
                                style={styles.roundInfoRow}
                              >
                                {`${row.playerName}: ${row.points}`}
                              </Text>
                            ))}
                          </View>
                        ))
                      )}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Holes (${roundInfoResult.holes.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.holes.map((hole) => (
                        <Text key={`round-info-hole-${hole.number}`} selectable style={styles.roundInfoRow}>
                          {`#${hole.number}  ·  par ${hole.par ?? '—'}  ·  SI ${hole.strokeIndex ?? '—'}${hole.id ? `  ·  ${hole.id}` : ''}`}
                        </Text>
                      ))}
                    </View>
                    {/* eslint-enable i18n/no-literal-text */}
                  </ScrollView>
                )}
                <View style={styles.scoreModalButtonRow}>
                  {roundInfoResult ? (
                    <Pressable style={styles.scoreModalSecondaryButton} onPress={copyRoundInfo}>
                      <Text style={styles.scoreModalOkText}>{t('menu.roundInfoCopy')}</Text>
                    </Pressable>
                  ) : null}
                  <Pressable style={styles.scoreModalOkButton} onPress={() => setRoundInfoOpen(false)}>
                    <Text style={styles.scoreModalOkText}>{t('menu.close')}</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </Modal>
          <Modal
            transparent
            visible={bugReportOpen}
            animationType="fade"
            onRequestClose={() => setBugReportOpen(false)}
          >
            <Pressable style={styles.leftMenuBackdrop} onPress={() => setBugReportOpen(false)}>
              <Pressable style={styles.bugModalCard} onPress={() => null}>
                <Text style={styles.bugModalTitle}>{t('menu.reportBugTitle')}</Text>
                <Text style={styles.bugModalHint}>{t('menu.reportBugHint')}</Text>
                <TextInput
                  value={bugReportDescription}
                  onChangeText={setBugReportDescription}
                  multiline
                  numberOfLines={5}
                  style={styles.bugModalInput}
                  placeholder={t('menu.reportBugDescriptionPlaceholder')}
                  placeholderTextColor={appColors.text.muted}
                  editable={!bugReportSubmitting}
                />
                <View style={styles.bugModalRow}>
                  <MicButton
                    value={bugReportDescription}
                    onChangeText={setBugReportDescription}
                    disabled={bugReportSubmitting}
                    onError={setBugReportError}
                    testID="bug-report-mic"
                  />
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={openBugFilePicker}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.reportBugAddImage')}</Text>
                  </Pressable>
                  {bugReportImage ? (
                    <Pressable
                      style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                      onPress={clearBugImage}
                      disabled={bugReportSubmitting}
                    >
                      <Text style={styles.bugModalButtonText}>{t('menu.reportBugRemoveImage')}</Text>
                    </Pressable>
                  ) : null}
                </View>
                {bugReportImage ? (
                  <View style={styles.bugPreviewCard}>
                    <Image source={{ uri: bugReportImage.previewUrl }} style={styles.bugPreviewImage} resizeMode="cover" />
                    <Text style={styles.bugPreviewMeta}>{bugReportImageMeta}</Text>
                  </View>
                ) : (
                  <Text style={styles.bugModalHint}>{t('menu.reportBugPasteHint')}</Text>
                )}
                {bugReportError ? <Text style={styles.bugModalError}>{bugReportError}</Text> : null}
                <View style={styles.bugModalRow}>
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={() => setBugReportOpen(false)}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.close')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.bugModalButtonPrimary, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={submitBugReport}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{bugReportSubmitting ? t('menu.reportBugSending') : t('menu.reportBugSend')}</Text>
                  </Pressable>
                </View>
              </Pressable>
            </Pressable>
          </Modal>
          <Stack
            screenOptions={{
              headerStyle: {
                backgroundColor: appColors.background.app,
              },
              headerShadowVisible: false,
              headerTitleAlign: 'center',
              headerTintColor: appColors.text.heading,
              headerTitleStyle: {
                color: appColors.text.heading,
                fontSize: headerTitleFontSize,
                fontWeight: '700',
                letterSpacing: 0.2,
                ...Platform.select({
                  web: { fontFamily: appFonts.webSerif },
                  default: { fontFamily: appFonts.systemSerif },
                }),
              },
              contentStyle: {
                backgroundColor: appColors.background.app,
              },
              headerLeft: () =>
                !showHeaderMenu ? null : (
                  <View style={styles.headerLeft}>
                    <Pressable
                      style={styles.headerIconButton}
                      onPress={() => {
                        setAdminMenuOpen(false);
                        setLeftMenuOpen((prev) => !prev);
                      }}
                    >
                      <View style={styles.headerMenuIcon} accessibilityRole="image">
                        <View style={styles.headerMenuBar} />
                        <View style={styles.headerMenuBar} />
                        <View style={styles.headerMenuBar} />
                      </View>
                    </Pressable>
                    {canViewAs ? (
                      <Pressable
                        style={styles.headerAdminButton}
                        testID="header-admin-menu"
                        onPress={() => {
                          setLeftMenuOpen(false);
                          setAdminMenuOpen((prev) => !prev);
                        }}
                        accessibilityLabel={t('menu.adminTools')}
                      >
                        <FontAwesome name="cog" size={18} color={appColors.text.heading} />
                      </Pressable>
                    ) : null}
                  </View>
                ),
              headerRight: () => (
                isResultsView ? (
                  // A shared results link stays chrome-free by design; the beta marker is the one
                  // exception, because it is a statement about the whole app.
                  <View style={[styles.headerRight, compactWebHeader && styles.headerRightCompact]}>
                    <BetaBadge compact={compactWebHeader} />
                  </View>
                ) : isPublicInviteView ? (
                  <View style={[styles.headerRight, compactWebHeader && styles.headerRightCompact]}>
                    <BetaBadge compact={compactWebHeader} />
                    <Pressable
                      style={[styles.loginButton, compactWebHeader && styles.loginButtonCompact]}
                      onPress={() => {
                        void handleRedirect();
                      }}
                      accessibilityLabel={t('auth.loginTitle')}
                    >
                      <Text style={styles.loginButtonText}>{t('auth.loginTitle')}</Text>
                    </Pressable>
                  </View>
                ) : (
                  <View style={[styles.headerRight, compactWebHeader && styles.headerRightCompact]}>
                    <BetaBadge compact={compactWebHeader} />
                    {debugEnabled && !compactWebHeader ? (
                      <Pressable
                        style={styles.debugButton}
                        onPress={() => setDebugOpen(true)}
                        accessibilityLabel={t('debug.buttonLabel')}
                      >
                        <Text style={styles.debugButtonText}>{t('debug.button')}</Text>
                      </Pressable>
                    ) : null}
                    {canCheckServiceHealth && !compactWebHeader ? (
                      serviceStatusMissing.length > 0 || serviceStatusFetchFailed ? (
                        <Pressable
                          style={[
                            styles.serviceStatusButtonCritical,
                            serviceStatusFetchFailed && !serviceStatusFlashOn ? styles.serviceStatusButtonCriticalDim : null,
                          ]}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : serviceStatusHasWarnings ? (
                        <Pressable
                          style={styles.serviceStatusButtonWarning}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : null
                    ) : null}
                    <HeaderUser
                      compact={compactWebHeader}
                      hideProfileAndFriends={isAnonymousMode}
                      onOpenBugReport={() => {
                        setBugReportError(null);
                        setBugReportOpen(true);
                      }}
                    />
                  </View>
                )
              ),
            }}
          >
            <Stack.Screen name="index" options={{ href: null, headerShown: false }} />
            <Stack.Screen name="login" options={{ title: t('auth.loginTitle'), headerShown: false }} />
            <Stack.Screen name="register" options={{ title: t('register.title'), headerShown: false }} />
            <Stack.Screen name="welcome" options={{ title: t('welcome.title') }} />
            <Stack.Screen name="privacy" options={{ title: t('privacy.title') }} />
            <Stack.Screen name="terms" options={{ title: t('terms.title') }} />
            <Stack.Screen name="about" options={{ title: t('about.title') }} />
            <Stack.Screen name="delete-account" options={{ title: t('deleteAccountPage.title') }} />
            <Stack.Screen
              name="my-tours"
              options={{
                title: t('welcome.myToursNav'),
                headerTitleContainerStyle: {
                  left: 5,
                },
              }}
            />
            <Stack.Screen name="friends" options={{ title: t('friends.title') }} />
            <Stack.Screen name="referee" options={{ title: t('referee.title') }} />
            <Stack.Screen name="statistics" options={{ title: t('statistics.title') }} />
            <Stack.Screen name="my-rounds" options={{ title: t('myRounds.title') }} />
            <Stack.Screen name="log-round" options={{ title: t('logRound.title') }} />
            <Stack.Screen name="round/[roundId]/log-total-score" options={{ title: t('logTotalScore.title') }} />
            <Stack.Screen name="round/[roundId]/log-scorecard" options={{ title: t('logScorecard.title') }} />
            <Stack.Screen name="modal" options={{ presentation: 'modal', title: t('modal.title') }} />
            <Stack.Screen name="debug/deeplink" options={{ title: t('debug.deeplinkTitle') }} />
            <Stack.Screen name="round/[roundId]/select-games" options={{ title: t('selectGames.title') }} />
            <Stack.Screen name="round/[roundId]/roundstart" options={{ title: t('roundStartSetup.title') }} />
            <Stack.Screen name="round/[roundId]/mystery-prize" options={{ title: t('mysteryPrize.title') }} />
            <Stack.Screen name="round/[roundId]/freeze" options={{ title: t('freeze.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/compact" options={{ title: t('hole.title'), animation: 'slide_from_right' }} />
            <Stack.Screen name="tour/[tourId]/leaderboard" options={{ title: t('tourLeaderboard.title') }} />
            <Stack.Screen name="tour/[tourId]/photos" options={{ title: t('tourPhotos.title') }} />
            <Stack.Screen name="tour/invite/[inviteToken]" options={{ title: t('tourInvite.title') }} />
            <Stack.Screen name="friend/invite/index" options={{ title: t('friendInvite.title') }} />
            <Stack.Screen name="round/[roundId]/summary" options={{ title: t('roundResults.title') }} />
            <Stack.Screen name="round/[roundId]/rsvp" options={{ title: t('rsvp.title') }} />
            <Stack.Screen name="tour/[tourId]/round-init" options={{ title: t('players.roundInitTitle') }} />
            <Stack.Screen name="tour/[tourId]/schedule" options={{ title: t('schedule.title') }} />
            <Stack.Screen name="round/[roundId]/scorecard" options={{ title: t('scorecard.title') }} />
            {/* The live board is a wall display: the whole viewport is the board, and a header row
                would spend a tenth of the screen saying what anyone in the room can already see. */}
            <Stack.Screen name="round/[roundId]/board" options={{ headerShown: false }} />
            <Stack.Screen
              name="sheet/tour/[tourId]/leaderboard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/summary"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/scorecard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen name="tour/[tourId]/edit" options={{ headerShown: false }} />
            <Stack.Screen name="profile/index" options={{ title: t('profile.title') }} />
            <Stack.Screen name="config-course" options={{ title: t('courseConfig.title'), presentation: 'modal' }} />
          </Stack>
          {isHeaderlessShellView ? <BetaBadgeOverlay /> : null}
          <BetaInfoModal />
        </View>
        {showBottomMenu ? <BottomMenu /> : null}
      </View>
    </ThemeProvider>
  );
}

type LeftMenuProps = {
  open: boolean;
  isHoleRoute: boolean;
  canOpenRoundSettings: boolean;
  roundSettingsPath: string;
  scorerOnlyMode: boolean;
  isSingleRound: boolean;
  mysteryPath: string;
  canOpenMysteryPrize: boolean;
  tourPath: string;
  canOpenTourLeaderboard: boolean;
  tourLeaderboardPath: string;
  canManageCourses: boolean;
  canCreateAccount: boolean;
  onClose: () => void;
};

const LeftMenu = ({
  open,
  isHoleRoute,
  canOpenRoundSettings,
  roundSettingsPath,
  scorerOnlyMode,
  isSingleRound,
  mysteryPath,
  canOpenMysteryPrize,
  tourPath,
  canOpenTourLeaderboard,
  tourLeaderboardPath,
  canManageCourses,
  canCreateAccount,
  onClose,
}: LeftMenuProps) => {
  const router = useRouter();
  const { t } = useI18n();

  if (!open) return null;

  // A claim guest or invite viewer has no account, so none of the items below lead anywhere they
  // can go. Creating one is the whole menu for them.
  if (canCreateAccount) {
    return (
      <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
        <View style={styles.leftMenuCard}>
          <Pressable
            style={styles.leftMenuItem}
            testID="menu-create-account"
            onPress={() => {
              onClose();
              router.push('/register');
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.createAccount')}</Text>
          </Pressable>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
      <View style={styles.leftMenuCard}>
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push('/welcome');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.home')}</Text>
        </Pressable>
        <Pressable
          style={styles.leftMenuItem}
          testID="menu-referee"
          onPress={() => {
            onClose();
            router.push('/referee');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.referee')}</Text>
        </Pressable>
        {!scorerOnlyMode ? (
          <Pressable
            style={[styles.leftMenuItem, !canOpenRoundSettings && styles.leftMenuItemDisabled]}
            disabled={!canOpenRoundSettings}
            onPress={() => {
              onClose();
              router.push(roundSettingsPath);
            }}
          >
            <Text style={[styles.leftMenuItemText, !canOpenRoundSettings && styles.leftMenuItemTextDisabled]}>
              {t('tournaments.roundSettings')}
            </Text>
          </Pressable>
        ) : null}
        <Pressable
          style={[styles.leftMenuItem, !canOpenMysteryPrize && styles.leftMenuItemDisabled]}
          disabled={!canOpenMysteryPrize}
          onPress={() => {
            onClose();
            router.push(mysteryPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenMysteryPrize && styles.leftMenuItemTextDisabled]}>
            {t('mysteryPrize.title')}
          </Text>
        </Pressable>
        {!isSingleRound && !scorerOnlyMode ? (
          <Pressable
            style={styles.leftMenuItem}
            onPress={() => {
              onClose();
              router.push(tourPath);
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.tourSettings')}</Text>
          </Pressable>
        ) : null}
        {!isSingleRound && !scorerOnlyMode ? (
          <Pressable
            style={[styles.leftMenuItem, !canOpenTourLeaderboard && styles.leftMenuItemDisabled]}
            disabled={!canOpenTourLeaderboard}
            onPress={() => {
              onClose();
              router.push(tourLeaderboardPath);
            }}
          >
            <Text style={[styles.leftMenuItemText, !canOpenTourLeaderboard && styles.leftMenuItemTextDisabled]}>
              {t('menu.tourLeaderboard')}
            </Text>
          </Pressable>
        ) : null}
        {canManageCourses ? (
          <Pressable
            style={styles.leftMenuItem}
            onPress={() => {
              onClose();
              router.push('/config-course?manage=1');
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.golfCourses')}</Text>
          </Pressable>
        ) : null}
      </View>
    </Pressable>
  );
};

type AdminMenuProps = {
  open: boolean;
  isAdmin: boolean;
  isDeveloper: boolean;
  onGameCalculator: () => void;
  onGolfGamesAdmin: () => void;
  scoreAllHolesEnabled: boolean;
  scoreAllHolesRunning: boolean;
  scoreAllHolesStatus: string | null;
  onScoreAllHoles: () => void;
  roundInfoEnabled: boolean;
  roundInfoLoading: boolean;
  onRoundInfo: () => void;
  viewAsRole: ViewAsRole | null;
  onViewAs: () => void;
  onClose: () => void;
};

// Dropdown for the cog button right of the hamburger: everything in here is
// admin- or developer-only, so the button itself only renders for those roles
// (the real ones — "View as role" must stay reachable while simulating).
const AdminMenu = ({
  open,
  isAdmin,
  isDeveloper,
  onGameCalculator,
  onGolfGamesAdmin,
  scoreAllHolesEnabled,
  scoreAllHolesRunning,
  scoreAllHolesStatus,
  onScoreAllHoles,
  roundInfoEnabled,
  roundInfoLoading,
  onRoundInfo,
  viewAsRole,
  onViewAs,
  onClose,
}: AdminMenuProps) => {
  const router = useRouter();
  const { t } = useI18n();

  if (!open) return null;

  return (
    <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
      <View style={styles.adminMenuCard}>
        {isAdmin ? (
          <>
            <Pressable style={styles.leftMenuItem} testID="left-menu-game-calculator" onPress={onGameCalculator}>
              <Text style={styles.leftMenuItemText}>{t('menu.gameCalculator')}</Text>
            </Pressable>
            <Pressable style={styles.leftMenuItem} testID="left-menu-golf-games-admin" onPress={onGolfGamesAdmin}>
              <Text style={styles.leftMenuItemText}>{t('menu.golfGamesAdmin')}</Text>
            </Pressable>
            {/* Accepting derived tee/green positions decides what the platform treats as the
                real geometry, so it sits behind the admin gate like the other such tools. */}
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-course-mapping-review"
              onPress={() => {
                onClose();
                router.push('/course-mapping-review');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.courseMappingReview')}</Text>
            </Pressable>
            {/* Admin-only, unlike the "Manage subscription" entry below which developers get
                too — this lists every user's subscription, not just the caller's own. */}
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-payments-admin"
              onPress={() => {
                onClose();
                router.push('/subscription-admin');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.paymentsAdmin')}</Text>
            </Pressable>
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-golf-course-admin"
              onPress={() => {
                onClose();
                router.push('/golf-course-admin');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.golfCourseAdmin')}</Text>
            </Pressable>
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-admin-statistics"
              onPress={() => {
                onClose();
                router.push('/admin-statistics');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.adminStatistics')}</Text>
            </Pressable>
          </>
        ) : null}
        {isDeveloper ? (
          <>
            <Pressable
              style={[styles.leftMenuItem, (!scoreAllHolesEnabled || scoreAllHolesRunning) && styles.leftMenuItemDisabled]}
              disabled={!scoreAllHolesEnabled || scoreAllHolesRunning}
              onPress={onScoreAllHoles}
            >
              <Text
                style={[
                  styles.leftMenuItemText,
                  (!scoreAllHolesEnabled || scoreAllHolesRunning) && styles.leftMenuItemTextDisabled,
                ]}
              >
                {scoreAllHolesRunning ? t('menu.scoreAllHolesRunning') : t('menu.scoreAllHoles')}
              </Text>
            </Pressable>
            {scoreAllHolesStatus ? <Text style={styles.leftMenuHint}>{scoreAllHolesStatus}</Text> : null}
            <Pressable
              style={[styles.leftMenuItem, (!roundInfoEnabled || roundInfoLoading) && styles.leftMenuItemDisabled]}
              disabled={!roundInfoEnabled || roundInfoLoading}
              onPress={onRoundInfo}
            >
              <Text
                style={[
                  styles.leftMenuItemText,
                  (!roundInfoEnabled || roundInfoLoading) && styles.leftMenuItemTextDisabled,
                ]}
              >
                {roundInfoLoading ? t('menu.roundInfoLoading') : t('menu.roundInfo')}
              </Text>
            </Pressable>
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-batch-fleet"
              onPress={() => {
                onClose();
                router.push('/batch-fleet');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.batchFleet')}</Text>
            </Pressable>
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-ml-training"
              onPress={() => {
                onClose();
                router.push('/ml-training');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.mlTraining')}</Text>
            </Pressable>
            <Pressable
              style={styles.leftMenuItem}
              testID="left-menu-analysis-overview"
              onPress={() => {
                onClose();
                router.push('/analysis-overview');
              }}
            >
              <Text style={styles.leftMenuItemText}>{t('menu.analysisOverview')}</Text>
            </Pressable>
          </>
        ) : null}
        {isAdmin || isDeveloper ? (
          // The presentations shipped with the web app and the documents kept beside the code:
          // where to read about how the platform works, for the people who operate it.
          <Pressable
            style={styles.leftMenuItem}
            testID="left-menu-documentation"
            onPress={() => {
              onClose();
              router.push('/documentation');
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.documentation')}</Text>
          </Pressable>
        ) : null}
        {isAdmin || isDeveloper ? (
          // Dark-launched (see services/subscriptionFeature.ts): this entry reaches the
          // screen regardless of the flag, and the BFF marks these callers privileged so
          // payment-service answers real checkout/portal requests even while
          // Payments:SubscriptionsEnabled is off for everyone else.
          <Pressable
            style={styles.leftMenuItem}
            testID="left-menu-manage-subscription"
            onPress={() => {
              onClose();
              router.push('/subscription');
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.manageSubscription')}</Text>
          </Pressable>
        ) : null}
        {/* Cosmetic role simulation — the token is untouched, so anything the server
            gates still answers to the real roles. */}
        <Pressable style={styles.leftMenuItem} testID="menu-view-as" onPress={onViewAs}>
          <Text style={[styles.leftMenuItemText, viewAsRole && styles.viewAsActiveText]}>
            {viewAsRole ? t('menu.viewAsActive', { role: t(VIEW_AS_LABEL_KEYS[viewAsRole]) }) : t('menu.viewAs')}
          </Text>
        </Pressable>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  stack: {
    flex: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconButton: {
    marginLeft: 12,
    paddingHorizontal: 8,
    paddingVertical: 10,
  },
  headerAdminButton: {
    marginLeft: 4,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  headerMenuIcon: {
    width: 22,
    gap: 4,
  },
  headerMenuBar: {
    height: 2,
    borderRadius: 999,
    backgroundColor: appColors.text.heading,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  headerRightCompact: {
    gap: 4,
  },
  loginButton: {
    marginRight: 8,
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
    flexShrink: 1,
  },
  loginButtonCompact: {
    marginRight: 0,
    paddingHorizontal: 10,
  },
  loginButtonText: {
    color: appColors.text.secondary,
    fontWeight: '800',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  serviceStatusButtonCritical: {
    backgroundColor: appColors.status.danger,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.status.errorText,
  },
  serviceStatusButtonCriticalDim: {
    opacity: 0.35,
  },
  serviceStatusButtonWarning: {
    backgroundColor: appColors.status.warningStrong,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.accent.olive,
  },
  serviceStatusButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  debugButton: {
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  debugButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  leftMenuBackdrop: {
    flex: 1,
    backgroundColor: appColors.accent.overlay,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      },
    }),
  },
  leftMenuCard: {
    position: 'absolute',
    top: 52,
    left: 12,
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    minWidth: 170,
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  adminMenuCard: {
    position: 'absolute',
    top: 52,
    left: 58,
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    minWidth: 170,
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  leftMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  leftMenuItemDisabled: {
    opacity: 0.45,
  },
  leftMenuItemText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 14,
  },
  leftMenuItemTextDisabled: {
    color: appColors.text.muted,
  },
  viewAsActiveText: {
    color: appColors.accent.soft,
  },
  leftMenuHint: {
    color: appColors.text.muted,
    fontSize: 12,
    paddingHorizontal: 14,
    paddingBottom: 8,
  },
  scoreModalScroll: {
    marginTop: 10,
    maxHeight: 360,
  },
  scoreModalHole: {
    marginBottom: 10,
  },
  scoreModalHoleTitle: {
    color: appColors.text.heading,
    fontWeight: '700',
    fontSize: 14,
    marginBottom: 2,
  },
  scoreModalRow: {
    color: appColors.text.primary,
    fontSize: 13,
    paddingLeft: 8,
  },
  roundInfoHeading: {
    color: appColors.text.heading,
    fontWeight: '700',
    fontSize: 16,
    marginBottom: 6,
  },
  roundInfoRow: {
    color: appColors.text.primary,
    fontSize: 12,
    lineHeight: 17,
    paddingLeft: 8,
  },
  roundInfoGameBlock: {
    marginBottom: 6,
  },
  roundInfoGameName: {
    color: appColors.text.heading,
    fontSize: 12,
    fontWeight: '600',
  },
  scoreModalError: {
    color: appColors.status.danger,
    fontSize: 14,
    marginTop: 10,
  },
  scoreModalInput: {
    backgroundColor: appColors.background.panelAlt,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
  },
  scoreModalButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
  },
  scoreModalCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
  },
  scoreModalCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.surface.input,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreModalCheckboxChecked: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.accent,
  },
  scoreModalCheckboxMark: {
    color: appColors.text.inverse,
    fontWeight: '800',
    fontSize: 14,
    lineHeight: 16,
  },
  scoreModalCheckboxLabel: {
    color: appColors.text.primary,
    fontSize: 13,
    flexShrink: 1,
  },
  scoreModalImportHint: {
    color: appColors.text.muted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 12,
  },
  scoreModalSecondaryButton: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 9,
    marginTop: 14,
  },
  scoreModalOkButton: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.accent.positive,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 9,
    marginTop: 14,
  },
  scoreModalOkText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  bugModalCard: {
    position: 'absolute',
    top: 80,
    left: 16,
    right: 16,
    maxWidth: 520,
    marginHorizontal: 'auto',
    maxHeight: '82%',
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    padding: 14,
    gap: 10,
  },
  bugModalTitle: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '800',
  },
  bugModalHint: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  bugModalInput: {
    minHeight: 110,
    backgroundColor: appColors.background.panelAlt,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
    textAlignVertical: 'top',
  },
  bugModalRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  bugModalButton: {
    backgroundColor: appColors.background.interactiveMuted,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  bugModalButtonPrimary: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.accent.positive,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 'auto',
  },
  bugModalButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  bugPreviewCard: {
    borderRadius: 12,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    backgroundColor: appColors.background.panelAlt,
    padding: 8,
    gap: 6,
  },
  bugPreviewImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    backgroundColor: appColors.background.app,
  },
  bugPreviewMeta: {
    color: appColors.text.secondary,
    fontSize: 11,
  },
  bugModalError: {
    color: appColors.status.errorTextSoft,
    fontSize: 12,
    fontWeight: '600',
  },
});
